import defaults from "./locales/defaults.json";
export { isBuiltInDefaultSystemPrompt } from "./lib/system-prompt";
import type { Language } from "./lib/languages";
import { ui, localizedCopies } from "./lib/localization";
import { LANGUAGE_CONFIG } from "./lib/languages";
import type { Capability, ModelInfo } from "./types";

export type { Language } from "./lib/languages";

const createTranslations = (language: Language) => ({
  htmlLang: LANGUAGE_CONFIG[language].locale,
  languageName: LANGUAGE_CONFIG[language].name,
  switchLanguage: ui(language, "Language"),
  defaultConversation: defaults[language].title,
  defaultSystemPrompt: defaults[language].prompt,
  brandSubtitle: ui(language, "A lighter AI workspace"),
  closeSidebar: ui(language, "Close sidebar"),
  openSidebar: ui(language, "Open sidebar"),
  toggleSidebar: ui(language, "Toggle sidebar"),
  settings: ui(language, "Settings"),
  settingsGeneral: ui(language, "Conversation"),
  settingsMedia: ui(language, "Creation"),
  settingsAccount: ui(language, "Account & app"),
  newConversation: ui(language, "New conversation"),
  conversations: ui(language, "Conversations"),
  deleteConversation: ui(language, "Delete conversation"),
  localFirst: ui(language, "Local-first"),
  localFirstDetail: ui(language, "Conversation originals are stored on this device and on the server for context recovery. Permanent deletion also clears server conversation memory."),
  exportConversation: ui(language, "Export conversation"),
  productGuide: ui(language, "Explore NeuronDeck"),
  privacyPolicy: ui(language, "Privacy"),
  termsOfUse: ui(language, "Terms"),
  toggleTheme: ui(language, "Toggle theme"),
  toggleInspector: ui(language, "Toggle model inspector"),
  cloudflareAccount: ui(language, "Cloudflare account"),
  cloudflareConnected: ui(language, "Using your quota"),
  cloudflareAdminSiteQuota: ui(language, "Administrator connected; the workspace continues to use this site's public quota."),
  cloudflareSiteQuota: ui(language, "Using this site's quota"),
  cloudflarePublicPool: ui(language, "Public site quota"),
  cloudflareConnect: ui(language, "Connect Cloudflare"),
  cloudflareConnecting: ui(language, "Connecting…"),
  cloudflareDisconnect: ui(language, "Sign out and revoke access"),
  cloudflareSelectAccount: ui(language, "Choose the account to bill"),
  cloudflareAccountDescription: ui(language, "Once connected, chat, image generation, speech, and file processing use Workers AI quota from your selected Cloudflare account."),
  cloudflarePublicPoolDescription: ui(language, "Anonymous requests use the Cloudflare quota pool provided by this site; connecting an account switches to your own quota."),
  cloudflareAccountPrivacy: ui(language, "PKCE authorization; tokens are encrypted server-side and never stored in your browser."),
  cloudflareUnavailable: ui(language, "Cloudflare sign-in is not configured yet."),
  cloudflareAuthFailed: ui(language, "Cloudflare authorization failed. Please try again."),
  welcomeEyebrow: ui(language, "NeuronDeck"),
  welcomeTitle: ui(language, "Where shall we begin?"),
  welcomeDescription: ui(language, "A thought. A conversation. A little room for your next idea."),
  starterPrompts: [
    {
      label: ui(language, "Write some code"),
      description: ui(language, "Turn your next idea into something that runs."),
      prompt: ui(language, "I want to build a small program. Ask about its purpose, runtime, inputs, and outputs first, then provide runnable code."),
    },
    {
      label: ui(language, "Create an image"),
      description: ui(language, "A few words. A whole new perspective."),
      prompt: ui(language, "Create a fresh, refined natural-light illustration of a coffee and an open book by a window at dawn, with generous breathing room."),
    },
    {
      label: ui(language, "Chat freely"),
      description: ui(language, "Follow a thought. See where it takes you."),
      prompt: ui(language, "Let's have a relaxed chat. Start with one interesting, easygoing question."),
    },
  ],
  you: ui(language, "You"),
  generating: ui(language, "Generating"),
  recovering: ui(language, "Recovering connection"),
  reasoningTrace: ui(language, "Reasoning trace"),
  copied: ui(language, "Copied"),
  copy: ui(language, "Copy"),
  regenerate: ui(language, "Regenerate"),
  readAloud: ui(language, "Read aloud"),
  pauseSpeech: ui(language, "Pause"),
  resumeSpeech: ui(language, "Resume"),
  preparingSpeech: ui(language, "Preparing audio"),
  speechFailed: ui(language, "Speech generation failed. Try again shortly."),
  systemSpeechUnsupported: ui(language, "This browser does not support system text-to-speech."),
  systemSpeechVoiceUnavailable: ui(language, "This device has no system voice matching the text language. Install that language in system settings first."),
  editFromHere: ui(language, "Edit from here"),
  showMessageTime: ui(language, "Show message time"),
  messageModel: (name: string) => ui(language, "Message {0}", name),
  messageAria: ui(language, "Message"),
  stopGenerating: ui(language, "Stop generating"),
  sendMessage: ui(language, "Send message"),
  attachFiles: ui(language, "Add images or files"),
  attachmentDefaultPrompt: ui(language, "Please analyze the content I uploaded."),
  processingAttachment: ui(language, "Processing file…"),
  removeAttachment: (name: string) => ui(language, "Remove attachment: {0}", name),
  attachmentList: ui(language, "Attachments"),
  attachmentTruncated: ui(language, "Content truncated"),
  imageNeedsVision: ui(language, "This model cannot read images. Switch to a model marked Vision."),
  tooManyAttachments: ui(language, "You can add up to 4 attachments per message."),
  tooManyImages: ui(language, "This model has reached its image limit."),
  unsupportedAttachment: ui(language, "This file format is not supported."),
  imageTooLarge: ui(language, "Each image must be 4 MB or smaller."),
  documentTooLarge: ui(language, "Each file must be 5 MB or smaller."),
  attachmentReadFailed: ui(language, "This attachment could not be read. Try another file."),
  conversionFailed: ui(language, "The file could not be converted. Try another file or format."),
  modelCaution: ui(language, "Models can make mistakes. 10 generations per minute per visitor."),
  imageGenerating: ui(language, "Creating your image"),
  imageGeneratingWith: (name: string) => ui(language, "Rendering with {0}…", name),
  imageGenerationFailed: ui(language, "Image generation failed"),
  imageGenerationDuration: (duration: string) => ui(language, "Rendered in {0}", duration),
  imageGeneratedFallback: ui(language, "Your image is ready."),
  screenshotCapturedFallback: ui(language, "The webpage screenshot is ready."),
  fileGeneratedFallback: ui(language, "Your file is ready."),
  fileGenerationDuration: (duration: string) => ui(language, "Created in {0}", duration),
  fileExpiresIn24Hours: ui(language, "Temporary download expires after 24 hours"),
  downloadFile: (name: string) => ui(language, "Download file: {0}", name),
  openImage: ui(language, "Open full image"),
  downloadImage: ui(language, "Download image"),
  browserScreenshot: ui(language, "Web screenshot"),
  screenshotDuration: (duration: string) => ui(language, "Captured in {0}", duration),
  screenshotFullPage: ui(language, "Full page"),
  screenshotDesktop: ui(language, "Desktop viewport"),
  screenshotMobile: ui(language, "Mobile viewport"),
  openScreenshot: ui(language, "Open web screenshot"),
  downloadScreenshot: ui(language, "Download web screenshot"),
  researchSources: (count: number) => ui(language, "Sources consulted: {0}", count),
  researchSourceAccessed: (time: string) => ui(language, "Accessed {0}", time),
  researchPdfExport: ui(language, "Export cited PDF"),
  researchPdfExporting: ui(language, "Creating PDF"),
  researchPdfFailed: ui(language, "The research report PDF could not be created. Try again shortly."),
  imageModel: ui(language, "Image model"),
  imageModelDescription: ui(language, "New images use the selected model; edits, variations, and multi-reference requests automatically use FLUX.2 Dev with the real source pixels."),
  imageModelDefault: ui(language, "Default"),
  imageToolBadge: (name: string) => ui(language, "Image · {0}", name),
  speechModel: ui(language, "Read responses aloud"),
  pwaTitle: ui(language, "Install & completion alerts"),
  pwaDescription: ui(language, "Install NeuronDeck as a standalone app and get an alert when a generation finishes in the background."),
  pwaInstall: ui(language, "Install NeuronDeck"),
  pwaInstalled: ui(language, "Running as an installed app"),
  pwaInstallUnavailable: ui(language, "Use Add to Home Screen from your browser menu"),
  notificationsEnable: ui(language, "Enable completion alerts"),
  notificationsEnabled: ui(language, "Completion alerts enabled"),
  notificationsDenied: ui(language, "Notifications are blocked; allow them in site settings"),
  notificationsUnsupported: ui(language, "Completion alerts are not supported here"),
  speechModelDescription: ui(language, "Read assistant replies aloud. Chinese uses the device voice directly; long replies read up to the first 3,000 characters."),
  speechQuality: ui(language, "Natural voice first"),
  speechQualitySummary: ui(language, "Aura-2 for English and Spanish; device voices for Chinese and other languages"),
  speechQualityPrice: ui(language, "Aura-2 · Chinese uses no Cloudflare quota"),
  speechDevice: ui(language, "Device system voice"),
  speechDeviceSummary: ui(language, "Uses voices available on iOS, macOS, Android, and Windows"),
  speechDevicePrice: ui(language, "Free · This site does not call the Cloudflare speech API"),
  speechDefault: ui(language, "Default"),
  modelInspector: ui(language, "Model inspector"),
  closeInspector: ui(language, "Close inspector"),
  context: ui(language, "Context"),
  inputPerMillion: ui(language, "Input / M"),
  outputPerMillion: ui(language, "Output / M"),
  systemPrompt: ui(language, "System prompt"),
  customInstructions: ui(language, "Custom instructions"),
  customInstructionsPlaceholder: ui(language, "Optional: choose a response style, language, or format. Leave blank to use the defaults."),
  temperature: ui(language, "Temperature"),
  precise: ui(language, "Precise"),
  creative: ui(language, "Creative"),
  maxOutputTokens: ui(language, "Maximum output tokens"),
  outputTokenRecommendation: (recommended: string, maximum: string) => ui(language, "Recommended {0}; selectable up to {1}", recommended, maximum),
  resetOutputTokens: ui(language, "Use recommended"),
  capabilities: ui(language, "Capabilities"),
  text: ui(language, "text"),
  paid: ui(language, "paid"),
  browseAllModels: (count: number) => ui(language, "Browse all {0} models", count),
  modelLabel: ui(language, "Model"),
  exportYou: ui(language, "You"),
  emptyCompletion: ui(language, "The model completed without returning text."),
  generationStopped: ui(language, "Generation stopped."),
  generationInterrupted: ui(language, "Refreshing interrupted this text response. Existing content was kept; you can regenerate it."),
  generationFailed: ui(language, "The model could not complete this request."),
  requestFailed: (status: number) => ui(language, "Request failed with status {0}.", status),
  modelCatalogUnavailable: ui(language, "Model catalog unavailable"),
  picker: {
    dialogLabel: ui(language, "Choose a model"),
    eyebrow: ui(language, "Cloudflare model library"),
    title: ui(language, "Choose a model"),
    close: ui(language, "Close model picker"),
    searchPlaceholder: ui(language, "Search models, providers or capabilities"),
    searchAria: ui(language, "Search models"),
    filtersAria: ui(language, "Model filters"),
    filters: {
      all: ui(language, "All"),
      reasoning: ui(language, "Reasoning"),
      vision: ui(language, "Vision"),
      tools: ui(language, "Tools"),
      paid: ui(language, "Paid"),
      lora: ui(language, "LoRA"),
    },
    addFavorite: ui(language, "Add to favorites"),
    removeFavorite: ui(language, "Remove from favorites"),
    noResults: ui(language, "No models match this search."),
    modelCount: (visible: number, total: number) => ui(language, "{0} of {1} chat models", visible, total),
    catalogSynced: (date: string) => ui(language, "Catalog synced {0}", date),
    input: ui(language, "In"),
    output: ui(language, "Out"),
  },
  errors: {
    history_sync_required: ui(language, "Server context needs to be restored. Retry this response to upload the local history again."),
    conversation_busy: ui(language, "A response is already running in this conversation. Wait for it to finish or stop it."),
    tool_persistence_failed: ui(language, "A tool finished, but its recovery record could not be saved. Completed resources are preserved."),
    context_compaction_failed: ui(language, "Context could not be summarized. Your original messages are saved. Please retry."),
    output_limit: ui(language, "The model reached its output capacity after automatic recovery. Your partial answer is saved. Retry or choose another model."),
    reasoning_incomplete: ui(language, "The model ended without a final answer. Its reasoning is preserved. Retry or choose another model."),
    output_filtered: ui(language, "The model provider stopped this response. Edit the request or choose another model."),
    rate_limited: ui(language, "Too many requests. Please try again shortly."),
    image_job_persistence_failed: ui(language, "The image task could not be saved. Please retry."),
    image_cancel_pending: ui(language, "Image cancellation is not yet confirmed. Retry stopping the task."),
    ip_rate_limited: ui(language, "Too many requests from this network. Please wait and retry."),
    client_rate_limited: ui(language, "Too many requests. Please wait and retry."),
    concurrent_limit: ui(language, "The previous cross-conversation concurrency limit has been removed. Retry this response."),
    public_budget_exhausted: ui(language, "The previous site budget restriction has been removed. Retry this response."),
    resource_guard_unavailable: ui(language, "The quota service is temporarily unavailable. Please retry shortly."),
    resource_guard_busy: ui(language, "The quota service is busy. Please retry shortly."),
    inference_first_response_timeout: ui(language, "The model took too long to respond. Your content is preserved. Retry this request."),
    inference_idle_timeout: ui(language, "Model output stalled. Partial content is preserved. Please retry."),
    upstream_capacity: ui(language, "The model or account quota is busy. Retry shortly or choose another model."),
    request_tool_limit: ui(language, "This task reached its tool limit. Continue with a smaller request."),
    request_deadline_exceeded: ui(language, "The previous task time limit has been removed. Your completed work is saved. Retry this response."),
    network_error: ui(language, "The connection was interrupted. Content is preserved. Reconnect the original task."),
    task_interrupted: ui(language, "The server task was interrupted. Completed output is preserved; retry or continue the conversation."),
    invalid_model: ui(language, "Choose a valid Cloudflare chat model."),
    invalid_messages: ui(language, "The messages are invalid. Edit them and try again."),
    invalid_request: ui(language, "The request is invalid. Check it and try again."),
    inference_failed: ui(language, "Model inference failed. Try again or choose another model."),
    internal_error: ui(language, "The service is temporarily unavailable. Please try again."),
    image_not_supported: ui(language, "This model cannot read images. Choose a vision model."),
    too_many_attachments: ui(language, "There are too many attachments. Remove some and try again."),
    too_many_images: ui(language, "There are more images than this model can process."),
    attachment_too_large: ui(language, "An attachment is too large. Compress it or remove some content."),
    invalid_attachment: ui(language, "An attachment is invalid. Add it again."),
    unsupported_file: ui(language, "This file format is not supported."),
    conversion_failed: ui(language, "The file could not be converted."),
    file_too_large: ui(language, "The file is too large. Compress it and try again."),
    invalid_image_model: ui(language, "Choose a valid Cloudflare image model."),
    invalid_tts_model: ui(language, "Choose a valid Cloudflare speech model."),
    invalid_tts_text: ui(language, "There is no readable text in this reply."),
    invalid_tts_language: ui(language, "Speech is not available for this language."),
    tts_text_too_long: ui(language, "The speech text is too long. Shorten it and try again."),
    tts_rate_limited: ui(language, "Too many speech requests. Try again shortly."),
    tts_failed: ui(language, "Speech generation failed. Try again shortly."),
    oauth_expired: ui(language, "Your Cloudflare authorization expired. Connect again."),
    oauth_required: ui(language, "Connect a Cloudflare account first."),
    public_ai_pool_invalid: ui(language, "This site's public Cloudflare quota is temporarily unavailable."),
    public_ai_pool_busy: ui(language, "This site's public AI quota is busy. Please try again shortly."),
  },
});

export const translations = localizedCopies(createTranslations);


const modelDescriptionsZh: Record<string, string> = {
  "@cf/openai/gpt-oss-120b": "适合通用任务与智能体工作的生产级开放权重推理模型。",
  "@cf/google/gemma-2b-it-lora": "体积小巧、支持 LoRA 的 Gemma 指令模型。",
  "@cf/meta/llama-3.2-3b-instruct": "适合轻量对话与摘要的多语言模型。",
  "@cf/mistral/mistral-7b-instruct-v0.2-lora": "支持 LoRA 的 Mistral 指令微调模型。",
  "@cf/moonshotai/kimi-k2.7-code": "面向长程编程与智能体任务优化的前沿代码模型。",
  "@cf/deepseek-ai/deepseek-r1-distill-qwen-32b": "适合复杂分析与数学任务的蒸馏推理模型。",
  "@cf/meta/llama-3.1-8b-instruct-fp8": "适合日常对话的均衡型量化指令模型。",
  "@cf/meta/llama-3.2-1b-instruct": "适合轻量对话任务的高速多语言模型。",
  "@cf/moonshotai/kimi-k2.6": "支持长上下文推理与工具调用的前沿多模态模型。",
  "@cf/zai-org/glm-5.3": "智谱旗舰级智能体编程模型，支持超长上下文、推理、工具调用和结构化输出，适合多步骤开发任务。",
  "@cf/zai-org/glm-5.3-flash": "拥有超长上下文、视觉理解和工具调用能力的高性价比旗舰级 MoE 模型。",
  "@cf/zai-org/glm-4.7-flash": "支持多轮工具调用的高速多语言对话与指令模型。",
  "@cf/meta-llama/llama-2-7b-chat-hf-lora": "支持 LoRA、面向对话优化的 Llama 2 模型。",
  "@cf/meta/llama-3.3-70b-instruct-fp8-fast": "兼顾速度与高质量指令遵循的 FP8 Llama 模型。",
  "@cf/ibm-granite/granite-4.0-h-micro": "为函数调用和智能体工作流调优的紧凑指令模型。",
  "@cf/deepseek-ai/deepseek-v4-flash-0731": "面向高速推理与智能体任务的长上下文 DeepSeek 模型。",
  "@cf/qwen/qwen2.5-coder-32b-instruct": "面向代码生成、审查和调试的专用指令模型。",
  "@cf/zai-org/glm-5.2": "智谱旗舰级推理与智能体编程模型。",
  "@cf/nvidia/nemotron-3-120b-a12b": "为智能体 AI 系统打造的混合 MoE 推理模型。",
  "@cf/aisingapore/gemma-sea-lion-v4-27b-it": "专注东南亚语言的指令模型。",
  "@cf/qwen/qwen3-30b-a3b-fp8": "适合多语言推理与指令遵循的高效 MoE 模型。",
  "@cf/google/gemma-7b-it-lora": "支持 LoRA 的 Gemma 指令模型。",
  "@cf/google/gemma-4-26b-a4b-it": "拥有长上下文能力的 Google 高智能开放多模态模型。",
  "@cf/mistralai/mistral-small-3.1-24b-instruct": "文本能力出色的高效长上下文指令模型。",
  "@cf/deepseek-ai/deepseek-v4-pro-0813": "适合长程与复杂多步骤工作的高能力推理模型。",
  "@cf/meta/llama-3.2-11b-vision-instruct": "适合图像推理、描述和对话的视觉指令模型。",
  "@cf/qwen/qwen3.8-27b": "适合通用文本、推理和智能体工作的多模态指令模型。",
  "@cf/openai/gpt-oss-20b": "面向推理与专业任务的低延迟开放权重模型。",
  "@cf/meta/llama-4-scout-17b-16e-instruct": "用于文本和图像理解的原生多模态 MoE 模型。",
  "@cf/qwen/qwq-32b": "专注高难度多步骤问题的 Qwen 推理模型。",
};

export const getModelDescription = (model: ModelInfo, language: Language): string =>
  language === "zh" ? modelDescriptionsZh[model.id] ?? model.description : ui(language, model.description);

export const getCapabilityLabel = (capability: Capability, language: Language): string => {
  if (language === "en") return capability;
  return ui(language, { reasoning: "Reasoning", vision: "Vision", tools: "Tools", coding: "Coding" }[capability]);
};

export const getLocalizedError = (language: Language, code: string | undefined, fallback: string): string => {
  const messages = translations[language].errors as Record<string, string>;
  return (code && Object.hasOwn(messages, code) && messages[code]) || ui(language, fallback);
};

export const isDefaultConversationTitle = (title: string): boolean => title === "新建对话" || Object.values(defaults).some((copy) => title === copy.title);
